<script setup lang="ts">
import { ref, watch } from 'vue'

interface MentionItem {
  id: string
  fullName: string
  email?: string
}

const props = defineProps<{
  items: MentionItem[]
  command: (payload: { id: string; label: string }) => void
}>()

const selectedIndex = ref(0)

watch(() => props.items, () => {
  selectedIndex.value = 0
})

function selectItem(index: number) {
  const item = props.items[index]
  if (item) {
    props.command({ id: item.id, label: item.fullName })
  }
}

function onKeyDown(event: KeyboardEvent) {
  if (event.key === 'ArrowUp') {
    event.preventDefault()
    selectedIndex.value = (selectedIndex.value - 1 + props.items.length) % props.items.length
    return true
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    selectedIndex.value = (selectedIndex.value + 1) % props.items.length
    return true
  }

  if (event.key === 'Enter') {
    event.preventDefault()
    selectItem(selectedIndex.value)
    return true
  }

  return false
}

defineExpose({ onKeyDown })
</script>

<template>
  <div
    v-if="items.length"
    class="mention-list"
  >
    <div
      v-for="(item, index) in items"
      :key="item.id"
      class="mention-item"
      :class="{ 'is-selected': index === selectedIndex }"
      @click="selectItem(index)"
    >
      <v-avatar
        size="24"
        color="primary"
        class="mr-2"
      >
        <span class="text-caption">{{ item.fullName.charAt(0) }}</span>
      </v-avatar>
      <div class="mention-item-content">
        <div class="mention-name">
          {{ item.fullName }}
        </div>
        <div
          v-if="item.email"
          class="mention-email"
        >
          {{ item.email }}
        </div>
      </div>
    </div>
  </div>
  <div
    v-else
    class="mention-list mention-empty"
  >
    Сотрудник не найден
  </div>
</template>

<style scoped>
.mention-list {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-outline));
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  max-height: 200px;
  overflow-y: auto;
  padding: 4px;
  min-width: 200px;
}

.mention-item {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: pointer;
}

.mention-item:hover,
.mention-item.is-selected {
  background: rgb(var(--v-theme-primary), 0.1);
}

.mention-item-content {
  flex: 1;
  min-width: 0;
}

.mention-name {
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mention-email {
  font-size: 12px;
  color: rgb(var(--v-theme-on-surface-variant));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mention-empty {
  padding: 8px 12px;
  color: rgb(var(--v-theme-on-surface-variant));
  font-size: 13px;
}
</style>
